import React from "react";
import PropTypes from "prop-types";
import {useParams, useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {RiEditLine, RiPushpinLine, RiVolumeMuteLine, RiCloseLine} from "react-icons/ri";
import WS from "../../../ws";
import {getGroupStats, getGroupMembers, getGroupPinned, unpinGroupMessage} from "../../../Services/campaignFunctions";
import {groupLabel} from "../GroupListItem";
import {initials, avatarColorFor, groupPhotoUrl} from "../../helpers/avatar";
import GroupAvatar from "../GroupAvatar";
import {formatDuration} from "../../helpers/time";
import {memberLimit} from "../../helpers/memberLimit";
import {useGroupManager} from "../../Providers/groupManager";
import {
    Container, Header, Avatar, Name, Members, Actions, ActionButton,
    Section, SectionHead, SectionTitle, SeeAll, StatGrid, StatCard, StatLabel, StatValue, ComingSoon,
    MemberRow, MemberAvatar, MemberInfo, MemberName, MemberRole,
    PinnedCard, PinnedTitle, PinnedBody, PinnedRemove,
} from "./styles";

/**
 * Painel de infos do grupo (3ª coluna do Inbox). Sub-peça 1: cabeçalho + This Week
 * (Messages/Recebidas reais) + New Leads/Reply Time/Members/Pinned "em breve".
 * Montado com key={group.id} pelo Inbox -> reseta stats/seen a cada grupo.
 */
const GroupInfoPanel = ({group, pinVersion = 0}) => {
    const {t} = useTranslation();
    const {id: campaignId} = useParams();
    const history = useHistory();
    const {campaign, membersCountByGroup} = useGroupManager();
    const [stats, setStats] = React.useState(null);
    const [error, setError] = React.useState(false);
    const seenRef = React.useRef(new Set());
    const [members, setMembers] = React.useState(null);
    const [membersLoading, setMembersLoading] = React.useState(false);
    const [membersError, setMembersError] = React.useState(false);
    const [pinned, setPinned] = React.useState(null);
    const [pinnedLoading, setPinnedLoading] = React.useState(false);
    const [pinnedError, setPinnedError] = React.useState(false);
    const [pinReload, setPinReload] = React.useState(0);

    // busca as contagens ao abrir o grupo
    React.useEffect(() => {
        if (!group) return undefined;
        let alive = true;
        setError(false);
        setStats(null);
        (async () => {
            const res = await getGroupStats(campaignId, group.id);
            if (!alive) return;
            if (res?.isAxiosError || res instanceof Error) {
                setError(true);
            } else {
                setStats(res || {messages_week: 0, received_week: 0, new_leads: 0, reply_time_seconds: 0});
            }
        })();
        return () => {
            alive = false;
        };
    }, [group, campaignId]);

    // tempo real: conta msg nova do grupo (dedup por id).
    React.useEffect(() => {
        if (!group) return undefined;
        return WS.subscribe((msg) => {
            if (msg?.subject !== "group_message" || msg.data?.group_id !== group.id) return;
            const id = msg.data.id;
            if (id == null || seenRef.current.has(id)) return;
            seenRef.current.add(id);
            setStats((prev) => (prev ? {
                ...prev,
                messages_week: (prev.messages_week || 0) + 1,
                received_week: (prev.received_week || 0) + (msg.data.from_me === false ? 1 : 0),
            } : prev));
        });
    }, [group]);

    // busca os membros do grupo (effect separado do stats -> não bloqueiam um ao outro)
    React.useEffect(() => {
        if (!group) return undefined;
        let alive = true;
        setMembersError(false);
        setMembersLoading(true);
        setMembers(null);
        (async () => {
            const res = await getGroupMembers(campaignId, group.id);
            if (!alive) return;
            if (res?.isAxiosError || res instanceof Error) {
                setMembersError(true);
            } else {
                setMembers(Array.isArray(res) ? res : []);
            }
            setMembersLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [group, campaignId]);

    // fixadas: rebusca ao abrir, quando o Inbox avisa (pinVersion) ou ao remover (pinReload)
    React.useEffect(() => {
        if (!group) return undefined;
        let alive = true;
        setPinnedError(false);
        setPinnedLoading(true);
        (async () => {
            const res = await getGroupPinned(campaignId, group.id);
            if (!alive) return;
            if (res?.isAxiosError || res instanceof Error) {
                setPinnedError(true);
            } else {
                setPinned(Array.isArray(res) ? res : []);
            }
            setPinnedLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [group, campaignId, pinVersion, pinReload]);

    const handleUnpin = async (messageId) => {
        const res = await unpinGroupMessage(campaignId, group.id, messageId);
        if (!(res?.isAxiosError || res instanceof Error)) setPinReload((v) => v + 1);
    };

    if (!group) return <Container/>;

    const name = groupLabel(group);
    const show = (n) => (error ? "—" : (stats ? n : "…"));

    return (
        <Container>
            <Header>
                <Avatar $color={avatarColorFor(group.id)}><GroupAvatar name={name} photoUrl={groupPhotoUrl(campaign)}/></Avatar>
                <Name>{name}</Name>
                <Members>{membersCountByGroup?.[group.id] ?? group.members_count} / {memberLimit(campaign)} {t("words.members")}</Members>
                <Actions>
                    <ActionButton onClick={() => history.push(`/campaign/${campaignId}/manager/settings`)}><RiEditLine/> {t("words.edit")}</ActionButton>
                    <ActionButton disabled><RiPushpinLine/> {t("words.pin")}</ActionButton>
                    <ActionButton disabled><RiVolumeMuteLine/> {t("words.mute")}</ActionButton>
                </Actions>
            </Header>

            <Section>
                <SectionTitle>{t("words.this_week")}</SectionTitle>
                <StatGrid>
                    <StatCard>
                        <StatLabel>{t("words.messages")}</StatLabel>
                        <StatValue>{show(stats?.messages_week)}</StatValue>
                    </StatCard>
                    <StatCard>
                        <StatLabel>{t("words.new_leads")}</StatLabel>
                        <StatValue>{show(stats?.new_leads)}</StatValue>
                    </StatCard>
                    <StatCard>
                        <StatLabel>{t("words.reply_time")}</StatLabel>
                        <StatValue>{error ? "—" : (stats ? formatDuration(stats.reply_time_seconds) : "…")}</StatValue>
                    </StatCard>
                </StatGrid>
            </Section>

            <Section>
                <SectionHead>
                    <SectionTitle>{t("words.members")}</SectionTitle>
                    <SeeAll>{t("words.see_all")}</SeeAll>
                </SectionHead>
                {membersLoading && <ComingSoon>{t("words.loading")}</ComingSoon>}
                {!membersLoading && membersError && <ComingSoon>{t("words.something_wrong")}</ComingSoon>}
                {!membersLoading && !membersError && members && members.length === 0 && (
                    <ComingSoon>{t("words.no_members")}</ComingSoon>
                )}
                {!membersLoading && !membersError && members && members.slice(0, 5).map((m) => (
                    <MemberRow key={m.number}>
                        <MemberAvatar $color={avatarColorFor(m.number || m.name)}>{initials(m.name)}</MemberAvatar>
                        <MemberInfo>
                            <MemberName>{m.name}</MemberName>
                            <MemberRole>{m.is_admin ? t("words.administrator") : t("words.member")}</MemberRole>
                        </MemberInfo>
                    </MemberRow>
                ))}
            </Section>

            <Section>
                <SectionTitle>{t("words.pinned_message")}</SectionTitle>
                {pinnedLoading && <ComingSoon>{t("words.loading")}</ComingSoon>}
                {!pinnedLoading && pinnedError && <ComingSoon>{t("words.something_wrong")}</ComingSoon>}
                {!pinnedLoading && !pinnedError && pinned && pinned.length === 0 && (
                    <ComingSoon>{t("words.no_pinned_messages")}</ComingSoon>
                )}
                {!pinnedLoading && !pinnedError && pinned && pinned.map((m) => (
                    <PinnedCard key={m.id}>
                        <PinnedRemove onClick={() => handleUnpin(m.id)} aria-label={t("words.unpin")}><RiCloseLine/></PinnedRemove>
                        <PinnedTitle>{m.from_me ? t("words.you") : (m.from?.name || m.from?.jid || "")}</PinnedTitle>
                        <PinnedBody>{m.content}</PinnedBody>
                    </PinnedCard>
                ))}
            </Section>
        </Container>
    );
};

GroupInfoPanel.propTypes = {
    group: PropTypes.object,
    pinVersion: PropTypes.number,
};

export default GroupInfoPanel;
